import { countries } from '~/globals';

import stores from '~/consts/stores';
import { UTM_MEDIUM, UTM_MEDIUM_GROUPS, UTM_MEDIUM_INTERNAL_WHOLESALE } from '~/consts/utm';

export const LE_ENTITY_AUS = 'Australia';
export const LE_ENTITY_INTL = 'International';
export const LE_ENTITIES = [LE_ENTITY_AUS, LE_ENTITY_INTL];

export const CRUISE_ITEM_TYPE = 'cruise';
export const LE_LIVE_ITEM_TYPE = 'le_live';
export const DEFAULT_ITEM_TYPE = CRUISE_ITEM_TYPE;
export const LEBT_DEFAULT_ITEM_TYPE = 'hotel';
export const FLIGHT_ITEM_TYPE = 'flight';
export const FLIGHT_PROVIDER_OPTIONS = [
  { value: 'jetstar', label: 'Jetstar' },
  { value: 'sabre', label: 'Sabre' },
  { value: 'travelfusion', label: 'Travelfusion' },
  { value: 'charter', label: 'Charter' },
] as const;

export const OFFER_TYPES = [
  {
    value: 'flight',
    label: 'Flight',
    suppliers: [
      'Jetstar',
      'Qantas',
      'Virgin Australia',
      'Cebu Pacific',
      'Air Malaysia',
      'Cathay Pacific',
      'Singapore Airlines',
      'IndiGo',
      'British Airways',
      'Fiji Airways',
      'China Airlines',
      'Japan Airlines',
      'Other',
    ],
  },
  {
    value: 'ics_store',
    label: 'ICS Store',
    suppliers: ['ICS', 'Other'],
  },
  {
    value: LE_LIVE_ITEM_TYPE,
    label: 'LE LIVE',
    enabled: false,
    suppliers: ['Other'],
  },
  {
    value: 'cruise',
    label: 'Cruise',
    suppliers: [
      'AmaWaterways',
      'APT',
      'Azamara Cruises',
      'Carnival',
      'Celebrity',
      'Creative Cruising',
      'Crystal Cruises',
      'Cruising Power',
      'Cunard',
      'Disney',
      'Emerald Cruises',
      'Explora Journey',
      'Holland America',
      'Hurtigruten',
      'Ponant',
      'Princess Cruises',
      'Regent Seven Seas',
      'Revelex',
      'Royal Carribean',
      'Scenic Tours',
      'Seabourn',
      'Silverseas',
      'South Sea Cruises',
      'Travelmarvel',
      'Viking',
      'Virgin Voyages',
      'Other',
    ],
  },
  {
    value: 'hotel',
    label: 'Hotel',
    suppliers: [
      'Club Med',
      'Expedia TAAP',
      'Hotelbeds',
      'Island Escapes',
      'JTB',
      'Qatar Stopover',
      'RoomRes',
      'Stuba',
      'TBO',
      'Webbeds',
      'Other',
    ],
  },
  {
    value: 'tour',
    label: 'Tour',
    suppliers: [
      'AAT KINGS',
      'APT',
      'Backroads',
      'Bench Africa',
      'Contiki',
      'Cosmos',
      'Costsaver',
      'G Adventures',
      'Globus',
      'Insight Vacations',
      'Intrepid',
      'Journey Beyond',
      'JTB',
      'Klook',
      'Luxury Gold',
      'On the go',
      'Outback Spirits',
      'Rocky Mountaineer',
      'Scenic Tours',
      'Tauck',
      'The Africa Safari Co',
      'Titan Travel',
      'Trafalgar',
      'Travelmarvel',
      'Viator',
      'Other',
    ],
  },
  {
    value: 'experience',
    label: 'Experience',
    suppliers: [
      'Bench Africa',
      'Bula Taxi / Epic Fiji',
      'Daytrip Transfers',
      'Driveaway Holidays',
      'Expedia TAAP',
      'International Rail',
      'JTB',
      'Klook',
      'Mozio Transfers',
      'Qatar Stopover',
      'Rocky Mountaineer',
      'South Sea Cruises',
      'TBO',
      'Viator',
      'Other',
    ],
  },
  {
    value: 'other',
    label: 'Other',
    suppliers: [
      'Bench Africa',
      'Driveaway Holidays',
      'GDS',
      'Jetstar Groups',
      'Journey Beyond',
      'JTB',
      'Qatar Stopover',
      'Rocky Mountaineer',
      'Singapore Airlines',
      'The Africa Safari Co',
      'Virgin Groups',
      'Other',
    ],
  },
  {
    value: 'custom-offer-package',
    label: 'Custom Offer Package',
    suppliers: ['Other'],
  },
];

export const LEBT_OFFER_TYPES = OFFER_TYPES.filter((offerType) => {
  return !['cruise', 'tour', 'ics_store'].includes(offerType.value);
});

export const REASONS = [
  { value: 'vendor_not_on_le', label: "We don't have this vendor on LE" },
  { value: 'offer_not_on_le', label: "We don't have this offer on LE" },
  { value: 'tech_issues', label: 'I had tech issues booking this offer on LE' },
  { value: 'margin_max', label: 'Margin Max' },
  { value: 'other', label: 'Other' },
];

export const FLEXIBLE_CANCELLATION_POLICY = 'flexible';
export const NONREFUNDABLE_CANCELLATION_POLICY = 'nonrefundable';

export const CUSTOM_OFFER_CANCELLATION_POLICY_TYPES = [FLEXIBLE_CANCELLATION_POLICY, NONREFUNDABLE_CANCELLATION_POLICY];

export const PURCHASE_TYPE_SALES = 'sales';
export const PURCHASE_TYPE_POST_PURCHASE = 'post-purchase';
export type PurchaseType = typeof PURCHASE_TYPE_SALES | typeof PURCHASE_TYPE_POST_PURCHASE;

export const DEFAULT_CRUISE_POST_PURCHASE_LE_FEE = 50;

export const CRUISE_POST_PURCHASE_TYPES = [
  { value: 'date_change', label: 'Date Change' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'name_correction', label: 'Name Correction' },
  { value: 'change_of_sailing', label: 'Change of Sailing' },
  { value: 're_fare', label: 'Re-fare' },
  { value: 'add_passengers', label: 'Add Passenger(s)' },
  { value: 'add_ons', label: 'Add-ons' },
] as const;

export type CruisePostPurchaseType = (typeof CRUISE_POST_PURCHASE_TYPES)[number]['value'];
export const CRUISE_POST_PURCHASE_TYPE_VALUES = CRUISE_POST_PURCHASE_TYPES.map(({ value }) => value);

// Order-relationship type carried on `linked_orders`. Must stay in lockstep with
// svc-order's OrderRelationshipType, which the published contract types as a plain
// string rather than a union, so there is nothing to import yet.
export const FLIGHT_POST_PURCHASE_FEE_RELATIONSHIP = 'flight_post_purchase_fee';

export const POST_PURCHASE_TYPES = [
  { value: 'cancel_and_refund', label: 'Cancel And Refund' },
  { value: 'date_change', label: 'Date Change' },
  { value: 'credit_ticket_rebooking', label: 'Credit Ticket Rebooking' },
  { value: 'destination_change', label: 'Destination Change' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'name_correction', label: 'Name Correction' },
  { value: 'bags', label: 'Bags' },
  { value: 'advance_seats_selection', label: 'Advance Seats Selection' },
  { value: 'meals', label: 'Meals' },
] as const;

/**
 * The values svc-offer accepts for `post_purchase_type`, derived from the list above so a
 * rename here breaks every consumer at compile time instead of silently producing a value
 * the offer form's `oneOf` rejects.
 */
export type PostPurchaseType = (typeof POST_PURCHASE_TYPES)[number]['value'];
export const POST_PURCHASE_TYPE_VALUES = POST_PURCHASE_TYPES.map(({ value }) => value);

export const COUNTRIES_LIST = ['Unspecified', ...countries];

export const SALES_ATTRIBUTION_MARGIN_MAX_VALUE = 'Margin_Max';
export const SALES_ATTRIBUTION_CS_ERROR = 'cs_error';

const SALES_ATTRIBUTION_LIST = [
  { label: 'Contact Centre', value: UTM_MEDIUM, enabled: true },
  { label: 'Chadstone Store', value: stores.chadstone_store.utmMedium, enabled: true },
  { label: 'Bondi Store', value: stores.bondi_store.utmMedium, enabled: true },
  { label: stores.le_live.displayName, value: stores.le_live.utmMedium, enabled: false },
  { label: 'Margin Max', value: SALES_ATTRIBUTION_MARGIN_MAX_VALUE, enabled: true },
  { label: 'Group Bookings', value: UTM_MEDIUM_GROUPS, enabled: true },
  { label: 'Internal Wholesale', value: UTM_MEDIUM_INTERNAL_WHOLESALE, enabled: true },
  { label: 'CS Error', value: SALES_ATTRIBUTION_CS_ERROR, enabled: true },
];

export function getSalesAttributionList(selectedValue?: string) {
  return SALES_ATTRIBUTION_LIST.filter((option) => option.enabled || option.value === selectedValue);
}

export const WHOLESALE_AGENT_LIST = [
  'Ben Admon',
  'Deborah Caravello',
  'Tammy Cipriano',
  'Eleni Iordanidis',
  'Jenney Deane',
  'Matt McNeil',
  'Meaghan Couttas',
  'Melissa Crees',
  'Noel Regan',
  'Michael Finemore',
  'Other',
];

export const CONCIERGE_AGENT_LIST = [
  'Vicki Walls',
  'Leah Morrison',
  'Donna Lawrence',
  'Nicole Hughes',
  'Lenore Kennedy',
  'Other',
];
